import type { CategoryFilter, CategoryFiltersState } from '~/types/database'

interface DynamicOption {
  value: string
  label: string
  count: number
}

interface DynamicFilterOptionsResult {
  [filterKey: string]: DynamicOption[]
}

/**
 * Composable for fetching dynamic filter options based on product availability
 * Only shows options that have at least one product matching current filters
 */
export const useDynamicFilterOptions = () => {
  const supabase = useSupabaseClient()

  /**
   * Fetch available filter options for a category based on current filter state
   * @param categoryId - The category ID
   * @param filters - Array of filter definitions (sorted by display_order)
   * @param currentFilters - Current filter state
   * @returns Object mapping filter_key to available options with counts
   */
  const fetchDynamicOptions = async (
    categoryId: string,
    filters: CategoryFilter[],
    currentFilters: CategoryFiltersState
  ): Promise<DynamicFilterOptionsResult> => {
    const result: DynamicFilterOptionsResult = {}

    // Sort filters by display_order to ensure hierarchy
    const sortedFilters = [...filters].sort((a, b) => a.display_order - b.display_order)

    for (let i = 0; i < sortedFilters.length; i++) {
      const filter = sortedFilters[i]
      const filterKey = filter.filter_key

      // Build query with all parent filter constraints (lower display_order)
      let query = supabase
        .from('products')
        .select(filterKey, { count: 'exact', head: false })
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .not(filterKey, 'is', null)

      // Apply all parent filters (filters with lower display_order)
      for (let j = 0; j < i; j++) {
        const parentFilter = sortedFilters[j]
        const parentKey = parentFilter.filter_key
        const parentValue = currentFilters[parentKey]

        if (parentValue && parentValue !== 'all' && parentValue !== '') {
          if (Array.isArray(parentValue)) {
            query = query.in(parentKey, parentValue as string[])
          } else if (typeof parentValue === 'string') {
            query = query.eq(parentKey, parentValue)
          }
        }
      }

      // Execute query to get distinct values
      const { data, error } = await query

      if (error) {
        console.error(`Error fetching options for ${filterKey}:`, error)
        result[filterKey] = []
        continue
      }

      // Count occurrences of each value
      const valueCounts = new Map<string, number>()
      for (const row of data || []) {
        const value = row[filterKey]
        if (value && typeof value === 'string') {
          valueCounts.set(value, (valueCounts.get(value) || 0) + 1)
        }
      }

      // Convert to options array, sorted by count (desc) then label (asc)
      const options: DynamicOption[] = Array.from(valueCounts.entries())
        .map(([value, count]) => ({
          value,
          label: value,
          count
        }))
        .sort((a, b) => {
          // Sort by count descending, then by label ascending
          if (b.count !== a.count) return b.count - a.count
          return a.label.localeCompare(b.label)
        })

      result[filterKey] = options
    }

    return result
  }

  /**
   * Get child filter keys for a given filter (filters with higher display_order)
   */
  const getChildFilterKeys = (filters: CategoryFilter[], parentFilterKey: string): string[] => {
    const sortedFilters = [...filters].sort((a, b) => a.display_order - b.display_order)
    const parentIndex = sortedFilters.findIndex(f => f.filter_key === parentFilterKey)

    if (parentIndex === -1) return []

    return sortedFilters
      .slice(parentIndex + 1)
      .map(f => f.filter_key)
  }

  /**
   * Check if a filter is a parent filter (has children)
   */
  const isParentFilter = (filters: CategoryFilter[], filterKey: string): boolean => {
    const sortedFilters = [...filters].sort((a, b) => a.display_order - b.display_order)
    const filterIndex = sortedFilters.findIndex(f => f.filter_key === filterKey)
    return filterIndex < sortedFilters.length - 1
  }

  /**
   * Check if a filter has a parent
   */
  const hasParentFilter = (filters: CategoryFilter[], filterKey: string): boolean => {
    const sortedFilters = [...filters].sort((a, b) => a.display_order - b.display_order)
    const filterIndex = sortedFilters.findIndex(f => f.filter_key === filterKey)
    return filterIndex > 0
  }

  /**
   * Get the parent filter key for a given filter
   */
  const getParentFilterKey = (filters: CategoryFilter[], filterKey: string): string | null => {
    const sortedFilters = [...filters].sort((a, b) => a.display_order - b.display_order)
    const filterIndex = sortedFilters.findIndex(f => f.filter_key === filterKey)

    if (filterIndex <= 0) return null

    return sortedFilters[filterIndex - 1].filter_key
  }

  return {
    fetchDynamicOptions,
    getChildFilterKeys,
    isParentFilter,
    hasParentFilter,
    getParentFilterKey
  }
}
